import { memo, useCallback } from 'react';
import { useAzureProvider } from 'src/azure/context';

import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { appgamiAutomationAuthorizationUrl } from 'src/utils/azure-authorization-url.util';

interface Props {
  reloadtHttpAuthClientInit(args: { token: string, projectId: string; userId: string }, options?: {
		onSuccess?: () => void;
		onError?: () => void;
	}): void;
	isReloading: boolean;
}

let debounceTimeout: NodeJS.Timeout | null = null;

function AuthorizationPageMemo(props: Props) {
	const classes = useStyles();


	const { applicationContext: { projectId, userId, organizationName, organizationId, accessToken }} = useAzureProvider();
	const goToPermissions = useCallback(() => {
		const tabHeight = 600;
		const tabWidth = 920;

		const top = ((window.screen.height / 2) - tabHeight / 2);
		const left = ((window.screen.width / 2) + tabWidth);

		window.open(
			appgamiAutomationAuthorizationUrl({ organizationName, organizationId }),
			'mozillaWindow',
			`height=${tabHeight},width=${tabWidth},left=${left},top=${top}`
		);

		const callReloadAuthClientInit = () => {
			const clearDebounceTimeout = () => {
				if (debounceTimeout) {
					clearTimeout(debounceTimeout);
				}
			};

			props.reloadtHttpAuthClientInit({
				token: accessToken,
				projectId: projectId,
				userId: userId
			}, {
				onSuccess: () => {
					clearDebounceTimeout();
				},
				onError: () => {
					clearDebounceTimeout();
					debounceTimeout = setTimeout(callReloadAuthClientInit, 3000);
				},
			});
		};

		callReloadAuthClientInit();

	}, [projectId]);

	return (
		<div className={classes.root}>
			<div className={classes.content}>
				<h1 className={classes.title}>Let&apos;s Get You Set Up</h1>
				<p className={classes.body}>
					To run your automation rules, Appgami Automation needs permission to access your Azure DevOps API. This allows us to securely execute the rules you create on your behalf. Your data is handled securely and only used to execute your configured automation rules.
				</p>
				<div className={classes.bottom}>
					<Button
						appearance='primary'
						type='button'
						onClick={goToPermissions}
					>
							Authorize
					</Button>
				</div>
			</div>
		</div>
	);
}

const AuthorizationPage = memo(AuthorizationPageMemo);
export default AuthorizationPage;

const useStyles = makeStyles({
	root: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: '100vh',
		padding: tokens.spacingVerticalXXL,
		width: '100%'
	},
	content: {
		maxWidth: '520px',
		textAlign: 'center',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center'
	},
	title: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase600,
		fontWeight: tokens.fontWeightSemibold,
		lineHeight: tokens.lineHeightBase600,
		marginBottom: tokens.spacingVerticalM,
		marginTop: 0
	},
	body: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase300,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase400,
		margin: 0,
		marginBottom: tokens.spacingVerticalL
	},
	bottom: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: tokens.spacingVerticalS,
		paddingTop: tokens.spacingVerticalS
	},
	button: {
		gap: '8px'
	}
});